import { useEffect, useState } from "react";
import type { Locale } from "@shared/types/locale.ts";
import { useAsyncHandler } from "../../hooks/useAsyncHandler";
import { updateOwnProfile, uploadAvatar } from "../../lib/expert_insights_api";
import type { ContributorProfile, UpdateProfileBody } from "../../lib/expert_insights_api";
import { useUserContext } from "../../context/UserContext";
import { hasRole } from "../../lib/auth";
import { useLocale } from "../../context/LocaleProvider";

interface UseAccountResult {
  email: string | undefined;
  isExpert: boolean;
  profile: ContributorProfile | null;
  profileLoading: boolean;
  fullName: string;
  setFullName: (v: string) => void;
  credentials: string;
  setCredentials: (v: string) => void;
  bio: string;
  setBio: (v: string) => void;
  /** Saves everything the page edits: the expert profile and the language. */
  saveAccount: () => Promise<void>;
  canSave: boolean;
  saving: boolean;
  saveError: string | null;
  handleAvatarUpload: (file: File) => Promise<string | null>;
  uploading: boolean;
  uploadError: string | null;
  locale: Locale;
  changeLocale: (locale: Locale) => void;
}

/** Drives the unified account page. Loads contributor profile when the user has that role. */
export function useAccount(): UseAccountResult {
  const { session, loading: profileLoading, profile, refreshProfile } = useUserContext();
  const user = session?.user ?? null;
  const isExpert = !!user && hasRole(user, "expert_insights.expert");

  const [fullName, setFullName] = useState("");
  const [credentials, setCredentials] = useState("");
  const [bio, setBio] = useState("");

  useEffect(() => {
    if (!profile) return;
    setFullName(profile.fullName ?? "");
    setCredentials(profile.credentials ?? "");
    setBio(profile.bio ?? "");
  }, [profile]);

  const { locale, setLocale } = useLocale();
  const [chosenLocale, setChosenLocale] = useState<Locale>(locale);
  useEffect(() => setChosenLocale(locale), [locale]);

  const profileChanged =
    isExpert &&
    !!profile &&
    (fullName !== (profile.fullName ?? "") ||
      credentials !== (profile.credentials ?? "") ||
      bio !== (profile.bio ?? ""));
  const profileComplete = !isExpert || (!!fullName.trim() && !!credentials.trim() && !!bio.trim());
  const localeChanged = chosenLocale !== locale;

  const {
    loading: saving,
    error: saveError,
    handle: saveAccount,
  } = useAsyncHandler(async () => {
    if (profileChanged) {
      const body: UpdateProfileBody = {
        full_name: fullName.trim() || undefined,
        credentials: credentials.trim() || undefined,
        bio: bio.trim() || undefined,
      };
      await updateOwnProfile(body);
      await refreshProfile();
    }
    if (localeChanged) await setLocale(chosenLocale);
  });

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function handleAvatarUpload(file: File): Promise<string | null> {
    setUploading(true);
    setUploadError(null);
    try {
      const updated = await uploadAvatar(file);
      await refreshProfile();
      return updated.avatarUrl ?? null;
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : "Upload failed");
      return null;
    } finally {
      setUploading(false);
    }
  }

  return {
    email: user?.email ?? undefined,
    isExpert,
    profile,
    profileLoading,
    fullName,
    setFullName,
    credentials,
    setCredentials,
    bio,
    setBio,
    saveAccount,
    canSave: (profileChanged || localeChanged) && profileComplete && !saving && !uploading,
    saving,
    saveError,
    handleAvatarUpload,
    uploading,
    uploadError,
    locale: chosenLocale,
    changeLocale: setChosenLocale,
  };
}
